import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { faArrowRightFromBracket, faChevronsLeft, faPlus, faChevronsRight } from '@fortawesome/pro-regular-svg-icons';
import { KindeAngularService } from 'kinde-angular';
import { Observable, filter, map, mergeMap } from 'rxjs';

import { UserPermission } from '@eiq/auth';

import { CustomIcon, IconLibrary } from './utils/icons-library';
import { environment } from '../environments/environment';

const SMALL_SCREEN_BREAKPOINT = '(max-width: 991px)';

@Component({
  selector: 'mom-admin-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit {
  breakpointObserver = inject(BreakpointObserver);
  authService = inject(KindeAngularService);

  researchAssistantEnabled = environment.researchAssistantEnabled;

  activeParentRoute: string;
  faArrowRightFromBracket = faArrowRightFromBracket;
  faChevronsLeft = faChevronsLeft;
  faChevronsRight = faChevronsRight;
  faPlus = faPlus;
  userPermission = UserPermission;

  menuOpened$ = signal<boolean>(!this.breakpointObserver.isMatched(SMALL_SCREEN_BREAKPOINT));
  routeLoading$ = signal<boolean>(false);
  fullScreenPage$: Observable<boolean>;

  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private domSanitizer = inject(DomSanitizer);
  private matIconRegistry = inject(MatIconRegistry);
  private router = inject(Router);

  constructor() {
    this.registerIcons();
    this.fullScreenPage$ = this.isFullScreen();
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe(SMALL_SCREEN_BREAKPOINT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ matches }) => this.menuOpened$.set(!matches));
  }

  onLogout(): void {
    this.authService.logout();
  }

  onGoToExternal(externalUrl: string): void {
    window.open(externalUrl, '_blank');
  }

  async onNewProject(): Promise<void> {
    const { ProjectWizardComponent } = await import('@eiq/projects/f/wizard');
    this.dialog.open(ProjectWizardComponent, { autoFocus: 'dialog' });
  }

  private isFullScreen(): Observable<boolean> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        // Navigates to active component
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      mergeMap((route) => route.data),
      map((data) => data.fullScreen === true)
    );
  }

  private registerIcons(): void {
    IconLibrary.forEach((icon: CustomIcon) => {
      this.matIconRegistry.addSvgIcon(icon.svg, this.domSanitizer.bypassSecurityTrustResourceUrl(icon.path));
    });
  }
}
